import {
  ApacCandidateFooter,
  ApacCandidateHeader,
} from '@seek/apac-candidate-header-footer';
import { Box, Page, PageBlock, Stack, Text } from 'braid-design-system';
import { useParams } from 'react-router';
import type { Language } from '@seek/melways-sites';
import { useConfig } from '@seek/libs-shared';

import { useAuth } from '../../../shared/providers/authProvider';
import type { AppConfig } from '../../../shared/config';
import { getLanguageSwitcher, getLanguageUrl } from './languageSwitcher';

import * as styles from './MainLayout.css';
import { useFeatureFlags } from '../../../shared/providers/featureFlagProvider';

interface Props extends React.PropsWithChildren {
  pageWidth?: 'large' | 'medium' | undefined;
  showHeaderDivider?: boolean;
  showMarginTopAndBottom?: boolean;
}

export function MainLayout({
  children,
  pageWidth = 'large',
  showHeaderDivider = false,
  showMarginTopAndBottom = true,
}: Props) {
  const config = useConfig<AppConfig>();
  const urlParams = useParams<'locationSlug'>();

  const { authenticationStatus, userProfile } = useAuth();
  const { cookieFeatureFlags } = useFeatureFlags();

  const languageSwitcher = getLanguageSwitcher({ urlParams, config });

  const handleLanguageSwitch = (selectedLanguage: Language) =>
    // eslint-disable-next-line no-restricted-globals
    location.replace(
      getLanguageUrl({
        config,
        urlParams,
        selectedLanguage,
        // eslint-disable-next-line no-restricted-globals
        absoluteUrl: location.href,
      }),
    );

  return (
    <Page
      footer={
        <Box className={styles.footerWrapper}>
          {Object.values(cookieFeatureFlags).some((f) => f === true) && (
            <Box padding="large">
              <Text>
                Feature Flags enabled:{' '}
                {Object.entries(cookieFeatureFlags)
                  .filter((f) => f[1] === true)
                  .map((f) => f[0])
                  .join(', ')}
              </Text>
            </Box>
          )}
          <ApacCandidateFooter
            authenticationStatus={authenticationStatus}
            isExpandedVersion
          />
        </Box>
      }
      footerPosition="belowFold"
    >
      <Stack space={showMarginTopAndBottom ? 'large' : 'none'}>
        <ApacCandidateHeader
          authenticationStatus={authenticationStatus}
          userProfile={userProfile}
          returnUrlForPostAuth="/companies"
          activePrimaryTab="company"
          languageSwitcher={languageSwitcher}
          divider={showHeaderDivider}
          onLanguageSwitch={handleLanguageSwitch}
        />
        <PageBlock width={pageWidth}>
          <Box className={styles.body}>{children}</Box>
        </PageBlock>
      </Stack>
    </Page>
  );
}
